import React, { useMemo } from 'react';
import Tile from '../../../Components/Containers/Tile';
import { MetricsProvider, useMetricContext } from '../../Reports/Metrics/Provider';
import Loading from '../../../Components/Loading';
import ButtonBar from '../../../Components/Button/ButtonBar';
import { usePreference } from '../../../Data/Preference';
import Money from '../../../Components/Formatting/Money';
import { match } from '../../../Services/helpers';

const Finances = () => {
    const [selected, setSelected] = usePreference('home:finances:selected', 'invoices');
    const [timePeriod, setTimePeriod] = usePreference('home:finances:timePeriod', 'month');

    const dateRanges = {
        'day': { current: 'today', previous: 'yesterday' },
        'week': { current: 'thisWeek', previous: 'lastWeek' },
        'month': { current: 'thisMonth', previous: 'lastMonth' },
    };

    const { current: currentDateRange, previous: previousDateRange } = dateRanges[timePeriod] || dateRanges.month;

    const metrics = [
        {
            name: 'invoicesTotal',
            options: { dateRange: currentDateRange }
        },
        {
            name: 'invoicesTotal',
            options: { dateRange: previousDateRange }
        },
        {
            name: 'paymentsTotal',
            options: { dateRange: currentDateRange }
        },
        {
            name: 'paymentsTotal',
            options: { dateRange: previousDateRange }
        },
    ];

    const timePeriodButtons = [
        { label: 'Day', selected: timePeriod === 'day', onClick: () => setTimePeriod('day') },
        { label: 'Week', selected: timePeriod === 'week', onClick: () => setTimePeriod('week') },
        { label: 'Month', selected: timePeriod === 'month', onClick: () => setTimePeriod('month') },
    ];

    const metricTypeButtons = [
        { label: 'Invoices', selected: selected === 'invoices', onClick: () => setSelected('invoices') },
        { label: 'Payments', selected: selected === 'payments', onClick: () => setSelected('payments') },
    ];

    return (
        <Tile title="Finances" icon="CircleSterling">
            <div className="space-y-3">
                <div className="flex justify-end">
                    <ButtonBar size="xs" align="right" buttons={timePeriodButtons} />
                </div>

                <Inner {...{metrics, selected, currentDateRange}} />

                <ButtonBar size="sm" buttons={metricTypeButtons} />
            </div>
        </Tile>
    );
};

const Inner = ({metrics, selected, currentDateRange}) => {
    return (
        <MetricsProvider metrics={metrics} options={{ dateInterval: 'auto' }}>
            <Result selected={selected} currentDateRange={currentDateRange} />
        </MetricsProvider>
    );
};

const Result = ({selected, currentDateRange}) => {
    const { metrics, isLoading } = useMetricContext();

    const currentValue = useMemo(() => {
        if (!metrics) {
            return null;
        }
        const metricName = selected === 'invoices' ? 'invoicesTotal' : 'paymentsTotal';

        return metrics.find(m => m.name === metricName && m.options?.dateRange === currentDateRange);
    }, [metrics, selected, currentDateRange]);

    const previousValue = useMemo(() => {
        if (!metrics) {
            return null;
        }
        const metricName = selected === 'invoices' ? 'invoicesTotal' : 'paymentsTotal';

        return metrics.find(m => m.name === metricName && m.options?.dateRange !== currentDateRange);
    }, [metrics, selected, currentDateRange]);

    if (isLoading || !currentValue) {
        return <Loading size="2x" className="my-6" center />;
    }

    const formatValue = (val, dataType) => {
        return dataType?.toLowerCase() === 'currency' ? <Money pence={val} /> : val;
    };

    const periodLabel = match(currentDateRange, {
        'today': 'yesterday',
        'thisWeek': 'last week',
        'thisMonth': 'last month',
    }, 'previous period');

    const calculatePercentChange = () => {
        if (!previousValue || previousValue.result.data.value === 0) {
            return null;
        }

        const current = currentValue.result.data.value;
        const previous = previousValue.result.data.value;
        const change = ((current - previous) / previous) * 100;

        const roundedChange = Math.round(change * 10) / 10; // Round to 1 decimal place

        // Return null if the result is NaN or infinite
        return (isNaN(roundedChange) || !isFinite(roundedChange)) ? null : roundedChange;
    };

    const percentChange = calculatePercentChange();
    const isPositive = percentChange > 0;
    const isNegative = percentChange < 0;

    return (
        <div className="text-center py-6">
            <div className="text-4xl font-bold text-brand mb-2">
                {formatValue(currentValue.result.data.value, currentValue.result.dataType)}
            </div>
            {previousValue && (
                <div className="text-sm text-muted">
                    {formatValue(previousValue.result.data.value, previousValue.result.dataType)} {periodLabel}
                    {percentChange !== null && (
                        <span className={`ml-2 font-medium ${
                            isPositive ? 'text-green-600' :
                            isNegative ? 'text-red-600' :
                            'text-muted'
                        }`}>
                            {isPositive ? '+' : ''}{percentChange}%
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default Finances;
